import { Link } from "@inertiajs/react"
import { ArrowRight } from "lucide-react"
import { useTranslation } from "react-i18next"

import { CrewPulseEmpty } from "@/components/creator/crew-pulse-empty"
import { PulseRow } from "@/components/creator/pulse-row"
import { creatorCrewPulsePath } from "@/routes"
import type { CrewPulseEvent } from "@/types/collab"

interface CrewPulseSectionProps {
  events: CrewPulseEvent[]
}

/**
 * OKS-473 — Crew Pulse SECTION on the Crew tab root (UX Spec §2.1).
 *
 * A labelled preview ("Crew Pulse" + subhead) of the last few crew events with
 * a "View more →" link to the full stream. When the crew is quiet it shows the
 * "all quiet" empty block and HIDES "View more" (nothing to drill into). The
 * page is structured so §18 (OKS-474) can append leaders / roster / feed
 * sections below this one without restructuring.
 */
export function CrewPulseSection({ events }: CrewPulseSectionProps) {
  const { t } = useTranslation()
  const isEmpty = events.length === 0

  return (
    <section
      aria-labelledby="crew-pulse-heading"
      className="flex flex-col gap-1"
    >
      <div className="px-4">
        <h2 id="crew-pulse-heading" className="text-base font-semibold">
          {t("crew_pulse.section_label")}
        </h2>
        <p className="text-muted-foreground text-xs">
          {t("crew_pulse.subhead")}
        </p>
      </div>

      {isEmpty ? (
        <CrewPulseEmpty />
      ) : (
        <>
          <ul
            data-testid="crew-pulse-section-list"
            aria-live="polite"
            aria-label={t("crew_pulse.region_label")}
            className="divide-border/60 flex flex-col divide-y"
          >
            {events.map((event) => (
              <PulseRow key={event.id} event={event} />
            ))}
          </ul>
          <div className="px-4 pt-1">
            <Link
              href={creatorCrewPulsePath()}
              data-testid="crew-pulse-view-more"
              className="text-primary inline-flex items-center gap-1 py-2 text-sm font-medium"
            >
              {t("crew_pulse.view_more")}
              <ArrowRight aria-hidden="true" className="size-4" />
            </Link>
          </div>
        </>
      )}
    </section>
  )
}
